import React from 'react';
import { FACTOR_LABELS, RISK_LABEL } from './riskOptions';

// Renders a customer_risk_assessments row the way the "KYC and Risk
// Assessment Module" sheet lays it out: one block per section with its
// factors and rating, then the overall rating. Assessments made before the
// sectioned model only show the overall score and tier.

const pill = (tier) => (
  <span className={`un-meta__pill cust-pill--risk-${tier}`}>{RISK_LABEL[tier] || tier || '—'}</span>
);

function Section({ title, section, labels }) {
  if (!section) return null;
  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.4rem' }}>
        <strong>{title}</strong>
        <span style={{ fontSize: '0.85rem' }}>
          Risk rating {pill(section.tier)}{' '}
          <span style={{ color: 'var(--text-secondary, #607d8b)' }}>score {section.score}</span>
        </span>
      </div>
      <div className="table-container">
        <table className="pips-table">
          <thead>
            <tr>
              <th>Factor</th>
              <th>Value</th>
              <th>Risk</th>
              <th>Weight</th>
            </tr>
          </thead>
          <tbody>
            {/* JSONB doesn't keep key order — list factors in the sheet's order. */}
            {[
              ...Object.keys(labels).filter((k) => section.factors[k]),
              ...Object.keys(section.factors).filter((k) => !labels[k])
            ].map((key) => [key, section.factors[key]]).map(([key, factor]) => (
              <tr key={key}>
                <td>{labels[key] || key}</td>
                <td>{factor.label}</td>
                <td>{pill(factor.tier)}</td>
                <td>{factor.weight ? `${Math.round(factor.weight * 100)}%` : 'Flag'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {section.overrides?.length > 0 && section.scored_tier !== section.tier && (
        <p style={{ fontSize: '0.8rem', margin: '0.35rem 0 0', color: 'var(--text-secondary, #607d8b)' }}>
          Weighted score alone gives {RISK_LABEL[section.scored_tier]}; raised to High by a flag.
        </p>
      )}
    </div>
  );
}

function RiskAssessmentView({ assessment }) {
  const sections = assessment?.rationale?.sections;

  return (
    <>
      {sections && (
        <>
          <Section title="Client"       section={sections.client}       labels={FACTOR_LABELS.client} />
          <Section title="Product"      section={sections.product}      labels={FACTOR_LABELS.product} />
          <Section title="Legal Person" section={sections.legal_person} labels={FACTOR_LABELS.legal_person} />
          {!sections.product && (
            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary, #607d8b)' }}>
              Product not captured, so it is left out of the overall rating.
            </p>
          )}
        </>
      )}

      <div className="cust-detail-grid">
        <div className="cust-detail-field">
          <span className="cust-detail-label">Overall risk</span>
          <span className="cust-detail-value">{pill(assessment.risk_tier)}</span>
        </div>
        <div className="cust-detail-field">
          <span className="cust-detail-label">Overall score</span>
          <span className="cust-detail-value">{assessment.weighted_score}</span>
        </div>
        <div className="cust-detail-field">
          <span className="cust-detail-label">Assessment type</span>
          <span className="cust-detail-value">{assessment.assessment_type}</span>
        </div>
        <div className="cust-detail-field">
          <span className="cust-detail-label">Model</span>
          <span className="cust-detail-value">{assessment.model_version || 'Earlier model'}</span>
        </div>
      </div>

      {assessment.rationale?.overrides?.length > 0 && (
        <div className="alert-banner alert-banner--warning" style={{ marginTop: '0.75rem' }}>
          <span className="alert-banner__body">
            Rated High and Enhanced Due Diligence (EDD) required because: {assessment.rationale.overrides.join('; ')}.
          </span>
        </div>
      )}
    </>
  );
}

export default RiskAssessmentView;
